import { useCallback, useContext, useEffect } from 'react';

import { useLingui } from '@lingui/react/macro';
import { useAtomValue, useSetAtom } from 'jotai';
import { snakeCase } from 'lodash';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { ExternalPatientContext } from '@/app/external/constants';

import type {
  CALCULUS,
  CARIES,
  PERIAPICAL_RADIOLUCENCY,
  RESTORATIVE_ADJACENT_LUCENCY,
} from '../../../../constants';
import { useConfidenceThresholds } from '../../../../react-query/useConfidenceThresholds';
import { usePreferences } from '../../../../react-query/usePreferences';
import type { UseIncipientReturn } from '../../../../routes/IncipientContext';
import { useIncipient } from '../../../../routes/IncipientContext';
import { isWithinSensitivityRange } from '../../annotation-utils/incipient';
import { selectedFindingsAtom } from '../../casePresentation/atoms';
import type { ImageObject, UserAnnotation } from '../../types';

import { binFilterAtom, confidenceScoreFilterAtom } from './atoms';

const typeToThresholdLabel = {
  calculus: 'calculus',
  caries: 'caries',
  periapical_radiolucency: 'periapicalRadiolucency',
  restorative_adjacent_lucency: 'restorationAdjacentLucency',
} as const;

const binToThresholdMap = {
  0: 2,
  1: 4,
  2: 6,
  3: 7,
} as const;

export const useIsWithinConfidenceThreshold = () => {
  const { data } = useConfidenceThresholds();
  const binValue = useAtomValue(binFilterAtom);
  const confidenceScoreThreshold = useAtomValue(confidenceScoreFilterAtom);

  const isWithinConfidenceThreshold = useCallback(
    (
      type:
        | typeof CARIES
        | typeof RESTORATIVE_ADJACENT_LUCENCY
        | typeof PERIAPICAL_RADIOLUCENCY
        | typeof CALCULUS,
      confidenceScore?: number | null,
      bin?: number | null,
    ) => {
      if (
        bin !== null &&
        bin !== undefined &&
        binValue !== null &&
        binValue !== undefined
      ) {
        return bin <= binValue;
      }

      if (binValue !== null && binValue !== undefined) {
        const mappedThreshold =
          binToThresholdMap[binValue as keyof typeof binToThresholdMap];
        const thresholds = data?.find(
          (model) => model.label === typeToThresholdLabel[type],
        )?.confidenceThresholds;

        if (thresholds && confidenceScore) {
          return confidenceScore >= thresholds[mappedThreshold];
        }
      }

      if (typeof confidenceScoreThreshold !== 'number' || !confidenceScore) {
        return true;
      }
      const thresholds = data?.find(
        (model) => model.label === typeToThresholdLabel[type],
      )?.confidenceThresholds;
      if (!thresholds) {
        return true;
      }
      return confidenceScore >= thresholds[confidenceScoreThreshold];
    },
    [data, binValue, confidenceScoreThreshold],
  );

  return isWithinConfidenceThreshold;
};

export const useDoAnalysesHaveConfidenceScore = () => {
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const patients = useSelector((state: any) => state.annotator?.patients) || {};
  const currentPatient = patients[selectedPatientId] || {};
  const { t } = useLingui();

  const modelsWithScores: string[] = [];
  if (!currentPatient?.images) {
    return {
      modelsWithScores,
    };
  }

  const currentPatientImages: ImageObject[] | undefined =
    Object.values<ImageObject>(currentPatient.images);

  if (
    currentPatientImages.some(
      (image: ImageObject) =>
        image?.models?.caries?.findings?.length &&
        !!image.models.caries.findings[0].confidenceScore,
    )
  ) {
    modelsWithScores.push(t`Caries`);
  }
  if (
    currentPatientImages.some(
      (image: ImageObject) =>
        image?.models?.restorative_adjacent_lucency?.findings?.length &&
        !!image.models.restorative_adjacent_lucency.findings[0].confidenceScore,
    )
  ) {
    modelsWithScores.push(t`Restorative Imperfections`);
  }
  if (
    currentPatientImages.some(
      (image: ImageObject) =>
        image?.models?.periapical_radiolucency?.findings?.length &&
        !!image.models.periapical_radiolucency.findings[0].confidenceScore,
    )
  ) {
    modelsWithScores.push(t`Periapical Radiolucency`);
  }
  if (
    currentPatientImages.some(
      (image: ImageObject) =>
        image?.models?.calculus?.findings?.length &&
        !!image.models.calculus.findings[0].confidenceScore,
    )
  ) {
    modelsWithScores.push(t`Calculus`);
  }
  return {
    modelsWithScores,
  };
};

export const useResetConfidenceScoreWhenPatientChanges = () => {
  const { data } = usePreferences();
  const setConfidenceScoreFilter = useSetAtom(confidenceScoreFilterAtom);
  const setBinFilter = useSetAtom(binFilterAtom);
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const { patientId } = useContext(ExternalPatientContext);

  // If patient id changes, reset slider to practice default
  useEffect(() => {
    const resetConfidenceScore = () => {
      if (!data?.confidenceThreshold || (!selectedPatientId && !patientId)) {
        return;
      }

      setConfidenceScoreFilter(data.confidenceThreshold);
      setBinFilter(data?.binThreshold);
    };
    resetConfidenceScore();
    return () => {
      resetConfidenceScore();
    };
  }, [
    data?.confidenceThreshold,
    data?.binThreshold,
    selectedPatientId,
    setConfidenceScoreFilter,
    setBinFilter,
    patientId,
  ]);
};

enum ModelsToCount {
  Caries = 'caries',
  RestorativeAdjacentLucency = 'restorative_adjacent_lucency',
  PeriapicalRadiolucency = 'periapical_radiolucency',
  Calculus = 'calculus',
  RadiographicBoneloss = 'radiographic_boneloss',
}

type ModelsToCountTotals = {
  added: number;
  total: number;
  hidden: number;
  description: string;
};

export const useImageFindingCounts = (image: ImageObject | null) => {
  const selectedFindingBounds = useAtomValue(selectedFindingsAtom);
  const { incipientThresholdRange } = useIncipient() as UseIncipientReturn;
  const isWithinConfidenceThreshold = useIsWithinConfidenceThreshold();
  const { t } = useLingui();

  // Create a mapping for model type names to their display names
  const modelTypeToDisplayName = {
    [ModelsToCount.Caries]: t`Caries`,
    [ModelsToCount.RestorativeAdjacentLucency]: t`Restorative Adjacent Lucency`,
    [ModelsToCount.PeriapicalRadiolucency]: t`Periapical Radiolucency`,
    [ModelsToCount.Calculus]: t`Calculus`,
    [ModelsToCount.RadiographicBoneloss]: t`Radiographic Bone Level`,
  };

  const result: Record<string, ModelsToCountTotals> = {};

  if (!image || !image.models) {
    return {
      allFindingsTotal: 0,
      allFindingsHiddenTotal: 0,
      allFindingsAddedTotal: 0,
      findingsDescription: '',
      ...result,
    };
  }

  for (const model in ModelsToCount) {
    if (ModelsToCount.hasOwnProperty(model)) {
      const modelType = ModelsToCount[model as keyof typeof ModelsToCount];

      const imageModel = image.models[modelType as keyof typeof image.models];

      const modelFindings =
        !!imageModel?.findings && imageModel?.findings?.length > 0
          ? imageModel.findings
          : null;

      const modelFindingsFiltered =
        modelType === ModelsToCount.RadiographicBoneloss && !!modelFindings
          ? modelFindings.filter(
              (finding: any) =>
                finding.sides &&
                finding.sides.filter(
                  (reading: any) =>
                    reading.side !== 'center' &&
                    reading.points &&
                    reading.points.length >= 2,
                ),
            )
          : modelFindings;

      const modelTotalCount =
        !modelFindingsFiltered || !modelFindingsFiltered.length
          ? 0
          : modelFindingsFiltered.length;

      const modelHiddenCount =
        modelFindingsFiltered?.filter((finding: any) => {
          const isRblFindingSelected = finding.sides?.some((side: any) =>
            side.points?.some(
              (point: any) => point.id === selectedFindingBounds?.id,
            ),
          );
          const isFindingSelected =
            selectedFindingBounds?.type === 'rbl'
              ? isRblFindingSelected
              : selectedFindingBounds?.id === finding.id;

          let shouldFindingsBeHidden = !isWithinConfidenceThreshold(
            finding.type,
            finding.confidenceScore,
            finding?.bin,
          );
          if (!shouldFindingsBeHidden) {
            shouldFindingsBeHidden =
              finding.validation === 'Disagree' ||
              (finding.source === 'AI' &&
                !isWithinSensitivityRange(
                  finding,
                  image,
                  incipientThresholdRange,
                )) ||
              (!!selectedFindingBounds && !isFindingSelected);
          }
          return shouldFindingsBeHidden;
        })?.length || 0;

      const userAnnotations = image.userAnnotations.filter(
        (annotation: UserAnnotation) =>
          snakeCase(annotation.type) === modelType,
      );
      const modelAddedCount = userAnnotations.length;

      result[modelType] = {
        total: modelTotalCount - modelHiddenCount,
        hidden: modelHiddenCount,
        added: modelAddedCount,
        description:
          modelTotalCount > 0
            ? `${modelTotalCount - modelHiddenCount} ${
                modelTypeToDisplayName[
                  modelType as keyof typeof modelTypeToDisplayName
                ]
              } ${t`finding(s) shown`}, ${modelHiddenCount} ${t`hidden`}${
                modelAddedCount ? `, ${modelAddedCount} ${t`added`}` : ''
              }. `
            : '',
      };
    }
  }

  const allFindingsTotal = Object.values(result).reduce(
    (total, model) => total + model.total,
    0,
  );
  const allFindingsHiddenTotal = Object.values(result).reduce(
    (total, model) => total + model.hidden,
    0,
  );
  const allFindingsAddedTotal = Object.values(result).reduce(
    (total, model) => total + model.added,
    0,
  );
  let findingsDescription = '';
  for (const model in ModelsToCount) {
    const modelType = ModelsToCount[model as keyof typeof ModelsToCount];
    findingsDescription = findingsDescription + result[modelType].description;
  }

  const allTotals = {
    allFindingsTotal,
    allFindingsHiddenTotal,
    allFindingsAddedTotal,
    findingsDescription,
    ...result,
  };

  return allTotals;
};
